import { useEffect, useRef, useState } from 'react'

const SCROLL_THRESHOLD = 16

/**
 * Tracks scroll direction on `.main-scroll-area` and returns whether the
 * header should be hidden. Intended for mobile only — CSS handles the
 * desktop override via `xl:` responsive prefix.
 */
export function useScrollHide() {
  const [hidden, setHidden] = useState(false)
  const lastScrollY = useRef(0)
  const rafId = useRef<number | null>(null)

  useEffect(() => {
    const scrollEl = document.querySelector('.main-scroll-area') as HTMLElement | null
    if (!scrollEl) return

    const onScroll = () => {
      if (rafId.current != null) return
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null
        const currentY = scrollEl.scrollTop

        // Always show at or near the top (also catches iOS rubber-band negative values)
        if (currentY <= SCROLL_THRESHOLD) {
          setHidden(false)
          lastScrollY.current = Math.max(currentY, 0)
          return
        }

        if (currentY > lastScrollY.current) {
          setHidden(true)
        } else if (currentY < lastScrollY.current) {
          setHidden(false)
        }

        lastScrollY.current = currentY
      })
    }

    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      scrollEl.removeEventListener('scroll', onScroll)
      if (rafId.current != null) cancelAnimationFrame(rafId.current)
    }
  }, [])

  return hidden
}
